import React, { type PropsWithChildren, type KeyboardEvent, type ReactNode } from 'react';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages.tsx';

type FloatingToolbarContainerProps = {
	onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
	children?: ReactNode;
};

export const FloatingToolbarContainer = ({
	children,
	onKeyDown,
}: PropsWithChildren<FloatingToolbarContainerProps>) => {
	const { formatMessage } = useIntl();

	return (
		<Box
			role="toolbar"
			xcss={floatingContainerStyles}
			aria-label={formatMessage(messages.toolbarName)}
			onKeyDown={onKeyDown}
		>
			{children}
		</Box>
	);
};

export const FloatingToolbarContent = ({ children }: PropsWithChildren) => (
	<Inline xcss={contentContainerStyles} alignBlock="center">
		{children}
	</Inline>
);

export const FloatingToolbarSeparator = () => <Box xcss={separatorStyles} />;

export const FloatingToolbarSideContent = ({ children }: PropsWithChildren) => (
	<Box xcss={sideContentContainer}>{children}</Box>
);

export const ContentSlider = ({ children, show }: { children: React.ReactNode; show: boolean }) => (
	<ExitingPersistence>
		{show && (
			<SlideIn enterFrom="bottom" fade="inout">
				{(slideInProps) => (
					<div {...slideInProps}>
						<Box xcss={mainContainerStyles}>{children}</Box>
					</div>
				)}
			</SlideIn>
		)}
	</ExitingPersistence>
);

const floatingContainerStyles = xcss({
	padding: 'space.100',
	backgroundColor: 'color.background.neutral.bold.pressed',
	borderRadius: '6px',
	boxShadow: 'elevation.shadow.overlay',
});

const contentContainerStyles = xcss({
	height: '32px',
	color: 'color.text.inverse',
	fontVariantNumeric: 'tabular-nums',
});

const separatorStyles = xcss({
	height: '16px',
	width: '1px',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: '2px',
	marginLeft: 'space.100',
	marginRight: 'space.100',
});

const sideContentContainer = xcss({
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
});

const mainContainerStyles = xcss({
	pointerEvents: 'auto',
});
